import React, { useEffect, useRef, useState } from 'react';
import {
  OrganizationLeadPreferences,
  leadSuggestionService,
} from '../services/leadSuggestionService';
import { useVerticalConfig } from '../hooks/useVerticalConfig';
import type { ResolvedVerticalConfig } from '../services/verticalPackService';

const DEFAULT_RADIUS_MILES = 25;

const splitCommaInput = (value: string) =>
  value
    .split(/[,\n;]/)
    .map((item) => item.trim())
    .filter(Boolean);

const toggleInList = (list: string[], value: string) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

interface LeadPreferenceFormProps {
  readOnly?: boolean;
  /** @deprecated No longer used — source selection is automatic. */
  showSourceSelection?: boolean;
  /** @deprecated No longer used — advanced scoring is removed from the UI. */
  showScoringWeights?: boolean;
  onSaved?: (prefs: OrganizationLeadPreferences) => void;
  /** Test-only override for URL search params (e.g. ?broaden=1&radius=25). */
  deepLinkSearch?: string;
  /** Test-only override for resolved vertical config. */
  verticalConfigOverride?: ResolvedVerticalConfig | null;
}

type LeadPreferenceFormBodyProps = LeadPreferenceFormProps & {
  searchString: string;
};

const DAILY_COUNT_OPTIONS = [5, 10, 25, 50] as const;

const LeadPreferenceFormBody: React.FC<LeadPreferenceFormBodyProps> = ({
  readOnly = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showSourceSelection: _showSourceSelection,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showScoringWeights: _showScoringWeights,
  onSaved,
  searchString,
  verticalConfigOverride,
}) => {
  const searchParams = new URLSearchParams(searchString);
  const { config: fetchedVerticalConfig } = useVerticalConfig();
  const verticalConfig = verticalConfigOverride !== undefined ? verticalConfigOverride : fetchedVerticalConfig;
  const radiusInputRef = useRef<HTMLInputElement>(null);
  const geographySectionRef = useRef<HTMLDivElement>(null);
  const deepLinkHandledRef = useRef(false);
  const [prefs, setPrefs] = useState<OrganizationLeadPreferences | null>(null);
  const [customServicesInput, setCustomServicesInput] = useState('');
  const [customCustomerTypesInput, setCustomCustomerTypesInput] = useState('');
  const [countiesInput, setCountiesInput] = useState('');
  const [zipCodesInput, setZipCodesInput] = useState('');
  const [preferredRolesInput, setPreferredRolesInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showExtraGeography, setShowExtraGeography] = useState(false);
  const [extraAreas, setExtraAreas] = useState<string[]>([]);
  const [showAdvancedTargeting, setShowAdvancedTargeting] = useState(false);
  const [highlightGeography, setHighlightGeography] = useState(false);

  useEffect(() => {
    leadSuggestionService.getLeadPreferences().then((data) => {
      setPrefs(data);
      setCustomServicesInput((data.custom_services || []).join(', '));
      setCustomCustomerTypesInput((data.custom_customer_types || []).join(', '));
      setCountiesInput((data.counties || []).join(', '));
      setZipCodesInput((data.zip_codes || []).join(', '));
      setPreferredRolesInput((data.preferred_buyer_roles || []).join(', '));
      if ((data.counties || []).length > 0 || (data.zip_codes || []).length > 0) {
        setShowExtraGeography(true);
      }
      if ((data.target_locations || []).length > 0) {
        setExtraAreas(data.target_locations);
      }
      if ((data.preferred_buyer_roles || []).length > 0) {
        setShowAdvancedTargeting(true);
      }
    }).catch((err) => setError(err instanceof Error ? err.message : 'Failed to load preferences'));
  }, []);

  useEffect(() => {
    if (!prefs || !verticalConfig) return;
    setPrefs((current) => {
      if (!current) return current;
      const next = { ...current };
      let changed = false;
      if (current.radius_miles == null && verticalConfig.defaults.radius_miles) {
        next.radius_miles = verticalConfig.defaults.radius_miles;
        changed = true;
      }
      if (!current.target_company_sizes?.length && verticalConfig.defaults.target_company_sizes?.length) {
        next.target_company_sizes = verticalConfig.defaults.target_company_sizes;
        changed = true;
      }
      return changed ? next : current;
    });
  }, [prefs, verticalConfig]);

  useEffect(() => {
    if (!prefs || deepLinkHandledRef.current) return;
    const broaden = searchParams.get('broaden') === '1';
    const focus = searchParams.get('focus');
    const radiusParam = searchParams.get('radius');
    if (!broaden && focus !== 'geography' && !radiusParam) return;

    deepLinkHandledRef.current = true;
    if (broaden || focus === 'geography') {
      setShowExtraGeography(true);
    }
    if (radiusParam) {
      const radius = Number(radiusParam);
      if (!Number.isNaN(radius) && radius > 0) {
        setPrefs((current) => {
          if (!current || current.radius_miles === radius) return current;
          return { ...current, radius_miles: radius };
        });
      }
    }
    if (broaden || focus === 'geography') {
      window.setTimeout(() => {
        geographySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        setHighlightGeography(true);
        window.setTimeout(() => setHighlightGeography(false), 2000);
      }, 100);
    }
  }, [prefs, searchParams]);

  if (!prefs) {
    return <p className="text-sm text-fg-muted">{error || 'Loading preferences...'}</p>;
  }

  const presets = prefs.preset_options;
  const businessTypeOptions = verticalConfig?.taxonomy.business_types ?? [];
  const effectiveBusinessType =
    prefs.business_type || verticalConfig?.business_type_key || 'other';
  const allServiceOptions = verticalConfig?.taxonomy.services_offered
    ?? presets?.services_offered
    ?? [];
  const allCustomerTypeOptions = verticalConfig?.taxonomy.ideal_customer_types
    ?? presets?.ideal_customer_types
    ?? [];

  // Strict business-type filtering: only show relevant options for the selected type.
  // Falls back to all options when the type has no specific relevance defined (e.g. "other").
  const businessTypeRelevance = verticalConfig?.taxonomy.business_type_relevance?.[effectiveBusinessType]
    ?? { services_offered: [], ideal_customer_types: [] };

  const serviceOptions = businessTypeRelevance.services_offered.length > 0
    ? businessTypeRelevance.services_offered.filter((s) => allServiceOptions.includes(s))
    : allServiceOptions;

  const customerTypeOptions = businessTypeRelevance.ideal_customer_types.length > 0
    ? businessTypeRelevance.ideal_customer_types.filter((c) => allCustomerTypeOptions.includes(c))
    : allCustomerTypeOptions;

  const planCap = prefs.max_selectable_daily_lead_count || prefs.plan_daily_lead_limit || 10;
  const dailyOptions = prefs.daily_lead_count_options?.length
    ? prefs.daily_lead_count_options
    : [...DAILY_COUNT_OPTIONS];
  const selectedDailyCount = Math.min(
    prefs.daily_lead_suggestion_count || prefs.daily_lead_limit,
    planCap,
  );
  const allCompanySizes = verticalConfig?.taxonomy.target_company_sizes
    ?? presets?.target_company_sizes
    ?? [];
  const effectiveCompanySizes = prefs.target_company_sizes?.length
    ? prefs.target_company_sizes
    : allCompanySizes;
  const displayRadius = prefs.radius_miles ?? DEFAULT_RADIUS_MILES;

  const hasLocationSet = Boolean(
    (prefs.primary_city || '').trim()
    || (prefs.state || '').trim()
    || extraAreas.some((area) => area.trim())
    || (prefs.target_locations || []).some((loc) => String(loc).trim()),
  );

  const handleSave = async () => {
    if (readOnly) return;
    if (!hasLocationSet) {
      setError('Add at least one service area — city and state, or another area — before saving.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload: Partial<OrganizationLeadPreferences> = {
        ...prefs,
        custom_services: splitCommaInput(customServicesInput),
        custom_customer_types: splitCommaInput(customCustomerTypesInput),
        counties: splitCommaInput(countiesInput),
        zip_codes: splitCommaInput(zipCodesInput),
        preferred_buyer_roles: splitCommaInput(preferredRolesInput),
        target_locations: extraAreas.map((area) => area.trim()).filter(Boolean),
        target_company_sizes: effectiveCompanySizes,
        radius_miles: prefs.radius_miles ?? DEFAULT_RADIUS_MILES,
        daily_lead_limit: Math.min(prefs.daily_lead_suggestion_count || prefs.daily_lead_limit, planCap),
        enabled: true,
      };
      const updated = await leadSuggestionService.updateLeadPreferences(payload);
      setPrefs(updated);
      onSaved?.(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const renderMultiSelect = (
    label: string,
    options: string[],
    selected: string[],
    onChange: (next: string[]) => void,
  ) => (
    <div className="space-y-2">
      <span className="block text-sm font-medium text-fg">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            disabled={readOnly}
            onClick={() => onChange(toggleInList(selected, option))}
            className={`px-3 py-1.5 text-sm rounded-full border ${
              selected.includes(option)
                ? 'bg-primary text-primary-fg border-primary'
                : 'border-border text-fg-muted hover:text-fg'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-red-500">{error}</p>}

      <section className="space-y-4">
        <h3 className="text-base font-semibold text-fg">Lead Suggestion Preferences</h3>

        {businessTypeOptions.length > 0 && (
          <label className="block text-sm">
            <span className="block mb-1 font-medium text-fg">Business type</span>
            <select
              disabled={readOnly}
              value={effectiveBusinessType}
              onChange={(e) => {
                setPrefs({ ...prefs, business_type: e.target.value });
              }}
              className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
            >
              {businessTypeOptions.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-fg-muted">
              Filters services and customer types to show only what's relevant for your business.
            </p>
          </label>
        )}

        {renderMultiSelect(
          'Services offered',
          serviceOptions,
          prefs.services_offered || [],
          (next) => setPrefs({ ...prefs, services_offered: next }),
        )}
        <label className="block text-sm">
          <span className="text-fg-muted">Custom services</span>
          <input
            type="text"
            disabled={readOnly}
            value={customServicesInput}
            onChange={(e) => setCustomServicesInput(e.target.value)}
            placeholder="Specialty floor waxing, post-construction cleanup"
            className="mt-1 w-full rounded border border-border bg-app px-3 py-2 text-fg"
          />
        </label>

        {renderMultiSelect(
          'Ideal customer types',
          customerTypeOptions,
          prefs.ideal_customer_types || [],
          (next) => setPrefs({ ...prefs, ideal_customer_types: next, target_business_types: next }),
        )}

        {/* Daily rotation order — only shown when 2+ customer types are selected */}
        {(prefs.ideal_customer_types || []).length > 1 && (
          <div className="space-y-2">
            <span className="block text-sm font-medium text-fg">Daily rotation order</span>
            <p className="text-xs text-fg-muted">
              We search one customer type per day in this order, rotating daily for variety.
            </p>
            <div className="space-y-1">
              {(prefs.ideal_customer_types || []).map((type, idx) => (
                <div key={type} className="flex items-center gap-2">
                  <span className="text-xs text-fg-muted w-4 text-right">{idx + 1}.</span>
                  <span className="text-sm text-fg flex-1 bg-surface-muted rounded px-2 py-1">{type}</span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      disabled={readOnly || idx === 0}
                      onClick={() => {
                        const next = [...(prefs.ideal_customer_types || [])];
                        [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                        setPrefs({ ...prefs, ideal_customer_types: next, target_business_types: next });
                      }}
                      className="px-1.5 py-0.5 text-xs rounded border border-border text-fg-muted hover:text-fg disabled:opacity-30"
                      aria-label="Move up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      disabled={readOnly || idx === (prefs.ideal_customer_types || []).length - 1}
                      onClick={() => {
                        const next = [...(prefs.ideal_customer_types || [])];
                        [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
                        setPrefs({ ...prefs, ideal_customer_types: next, target_business_types: next });
                      }}
                      className="px-1.5 py-0.5 text-xs rounded border border-border text-fg-muted hover:text-fg disabled:opacity-30"
                      aria-label="Move down"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <label className="block text-sm">
          <span className="text-fg-muted">Custom customer types</span>
          <input
            type="text"
            disabled={readOnly}
            value={customCustomerTypesInput}
            onChange={(e) => setCustomCustomerTypesInput(e.target.value)}
            placeholder="Veterinary clinics, coworking spaces"
            className="mt-1 w-full rounded border border-border bg-app px-3 py-2 text-fg"
          />
        </label>

        <div
          ref={geographySectionRef}
          className={`space-y-3 rounded-lg transition-colors ${
            highlightGeography ? 'ring-2 ring-primary/40 bg-primary/5 p-2 -m-2' : ''
          }`}
        >
          <p className="text-sm font-medium text-fg">Service area</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block mb-1 text-fg-muted">Primary city</span>
              <input
                type="text"
                disabled={readOnly}
                value={prefs.primary_city || ''}
                onChange={(e) => setPrefs({ ...prefs, primary_city: e.target.value })}
                className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-fg-muted">State</span>
              <input
                type="text"
                disabled={readOnly}
                value={prefs.state || ''}
                onChange={(e) => setPrefs({ ...prefs, state: e.target.value })}
                className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
              />
            </label>
          </div>

          {extraAreas.map((area, index) => (
            <label key={`extra-area-${index}`} className="text-sm block">
              <span className="block mb-1 text-fg-muted">Additional area {index + 1}</span>
              <div className="flex gap-2">
                <input
                  type="text"
                  disabled={readOnly}
                  value={area}
                  onChange={(e) => {
                    const next = [...extraAreas];
                    next[index] = e.target.value;
                    setExtraAreas(next);
                  }}
                  placeholder="Dallas TX"
                  className="flex-1 rounded border border-border bg-app px-3 py-2 text-fg"
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => setExtraAreas(extraAreas.filter((_, i) => i !== index))}
                    className="px-3 py-2 text-sm rounded border border-border text-fg-muted hover:text-fg"
                  >
                    Remove
                  </button>
                )}
              </div>
            </label>
          ))}

          {!readOnly && (
            <button
              type="button"
              onClick={() => setExtraAreas([...extraAreas, ''])}
              className="text-sm text-primary hover:underline"
            >
              Add another area
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={() => setShowExtraGeography((open) => !open)}
          className="text-sm text-primary hover:underline"
        >
          {showExtraGeography ? 'Hide counties / ZIP codes / radius' : 'Add counties / ZIP codes / radius'}
        </button>
        {showExtraGeography && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pl-0 md:pl-2 border-l-2 border-border">
            <p className="md:col-span-2 text-xs text-fg-muted">
              Radius and counties both narrow results — use one or combine carefully.
            </p>
            <label className="text-sm md:col-span-2">
              <span className="block mb-1 text-fg-muted">Radius (miles)</span>
              <input
                ref={radiusInputRef}
                type="number"
                min={1}
                disabled={readOnly}
                value={displayRadius}
                onChange={(e) => setPrefs({ ...prefs, radius_miles: e.target.value ? Number(e.target.value) : null })}
                className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="block mb-1 text-fg-muted">Counties (comma-separated)</span>
              <input
                type="text"
                disabled={readOnly}
                value={countiesInput}
                onChange={(e) => setCountiesInput(e.target.value)}
                className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="block mb-1 text-fg-muted">Zip codes (comma-separated)</span>
              <input
                type="text"
                disabled={readOnly}
                value={zipCodesInput}
                onChange={(e) => setZipCodesInput(e.target.value)}
                className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
              />
            </label>
          </div>
        )}

        {renderMultiSelect(
          'Target company sizes',
          allCompanySizes,
          effectiveCompanySizes,
          (next) => setPrefs({ ...prefs, target_company_sizes: next }),
        )}

        <button
          type="button"
          onClick={() => setShowAdvancedTargeting((open) => !open)}
          className="text-sm text-primary hover:underline"
        >
          {showAdvancedTargeting ? 'Hide advanced targeting' : 'Advanced targeting (optional)'}
        </button>
        {showAdvancedTargeting && (
          <label className="text-sm block pl-0 md:pl-2 border-l-2 border-border">
            <span className="block mb-1 text-fg-muted">Preferred job titles (comma-separated)</span>
            <input
              type="text"
              disabled={readOnly}
              value={preferredRolesInput}
              onChange={(e) => setPreferredRolesInput(e.target.value)}
              placeholder="Owner, Office Manager, Facilities Director"
              className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
            />
          </label>
        )}

        <label className="text-sm block">
          <span className="block mb-1 text-fg-muted">Leads per day</span>
          <select
            disabled={readOnly}
            value={selectedDailyCount}
            onChange={(e) =>
              setPrefs({
                ...prefs,
                daily_lead_limit: Number(e.target.value),
                daily_lead_suggestion_count: Number(e.target.value),
              })
            }
            className="w-full rounded border border-border bg-app px-3 py-2 text-fg"
          >
            {dailyOptions.map((n) => {
              const enabled = n <= planCap;
              return (
                <option key={n} value={n} disabled={!enabled}>
                  {n}
                </option>
              );
            })}
          </select>
        </label>
      </section>

      {!readOnly && (
        <div className="flex justify-end">
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary px-4 py-2 text-sm">
            {saving ? 'Saving...' : 'Save preferences'}
          </button>
        </div>
      )}
    </div>
  );
};

const LeadPreferenceForm: React.FC<LeadPreferenceFormProps> = (props) => {
  const searchString = props.deepLinkSearch ?? (typeof window !== 'undefined' ? window.location.search : '');
  return <LeadPreferenceFormBody {...props} searchString={searchString} />;
};

export default LeadPreferenceForm;
